import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";
import {LibraryDomainUseDTO, LibraryDTO, LibraryGoalUseDTO, LibraryIndicatorUseDTO} from "../../../../models/library";
import React from "react";
import {useTranslation} from "react-i18next";
import {API, createApiConfig} from "../../../../utils/API";
import {Box} from "@material-ui/core";
import {DeleteActionButton, InfoActionButton, SwapVerticallyActionButton} from "../../../common/buttons";
import {LoadingBackdrop} from "../../../common/Loading";
import {useKeycloak} from "@react-keycloak/web";
import {ConfirmationDialog, InformationDialog} from "../../../common/dialogs";
import {AxiosError} from "axios";
import {IndicatorDialog} from "../../../values/dialogs/IndicatorDialog";
import ItemPositionInLibrary, {IndicatorPositionInLibrary} from "../util/LibraryItemPosition";
import {ErrorDeleteDTO, rectifyErrorDelete} from "../../../../models/error";
import {LibraryIndicatorRenderDelete} from "./delete/LibraryIndicatorRenderDelete";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        indicatorRow: {
            borderBottom: "1px solid #F9FAFB",
            backgroundColor: "#FAFAFA",
            padding: `${theme.spacing(0.875)}px ${theme.spacing(2)}px ${theme.spacing(0.875)}px ${theme.spacing(7)}px`,
            display: "flex",
            alignItems: "center",
        },
        dragHandle: {
            marginRight: theme.spacing(0.5),
            display: "flex",
            cursor: "move",
            color: "#9CA3AF",
        },
        indicatorItemName: {
            marginLeft: theme.spacing(0.5),
            fontSize: "0.8125rem",
            color: "#6B7280",
            display: "flex",
            alignItems: "center",
            flexGrow: 1,
        },
        infoButton: {},
    })
);

interface LibraryIndicatorUseRowProps {
    library: LibraryDTO;
    libraryDomainUse: LibraryDomainUseDTO;
    libraryGoalUse: LibraryGoalUseDTO;
    libraryIndicatorUse: LibraryIndicatorUseDTO;
    onUpdate: (library: LibraryDTO) => void;
    shiftingItemPosition: ItemPositionInLibrary | null;
    handleShiftStart: (itemPosition: IndicatorPositionInLibrary) => void;
}

interface LibraryIndicatorUseRowState {
    name: "info" | "init" | "edit" | "saving" | "delete_confirm" | "delete_inform" | "deleting" | "failed" | "deleted";
    error: ErrorDeleteDTO | null;
}

export const LibraryIndicatorUseRow: React.FC<LibraryIndicatorUseRowProps> = (props: LibraryIndicatorUseRowProps) => {
    //== Init =================================================================
    const classes = useStyles();
    const [t] = useTranslation("libraries");
    const {keycloak, initialized} = useKeycloak();
    const initState: LibraryIndicatorUseRowState = {
        name: "init",
        error: null,
    };
    const [state, setState] = React.useState(initState);
    //== Effects ==============================================================
    React.useEffect(() => {
        if (state.name === "deleting") {
            API.delete<LibraryDTO>(
                `/indicators/library-use/${props.libraryIndicatorUse.id}`,
                createApiConfig(keycloak, initialized)
            )
                .then((res) => {
                    props.onUpdate(res.data);
                    //setState({...state, name: "deleted"});
                })
                .catch((err: AxiosError) => {
                    if (err.response?.status === 400) {
                        const error: ErrorDeleteDTO = rectifyErrorDelete(err.response.data);
                        setState({...state, name: "delete_inform", error: error});
                    } else {
                        setState({...state, name: "failed"});
                    }
                });
        }
    }, [state, setState, props]);
    //== Handlers =============================================================
    const handleInfoOpen = () => {
        setState({...state, name: "info"});
    };
    const handleDelete = () => {
        setState({...state, name: "delete_confirm"});
    };
    const handleDeleteConfirm = () => {
        setState({...state, name: "deleting"});
    };
    const handleReset = () => {
        setState({...state, name: "init"});
    };
    const isShiftingOtherThanThisOne =
        props.shiftingItemPosition &&
        (props.shiftingItemPosition.domainIndex !== props.libraryDomainUse.orderNumber ||
            props.shiftingItemPosition.goalIndex !== props.libraryGoalUse.orderNumber ||
            props.shiftingItemPosition.indicatorIndex !== props.libraryIndicatorUse.orderNumber);
    //== Render ===============================================================
    return (
        <Box className={classes.indicatorRow}>
            <div className={classes.dragHandle} style={isShiftingOtherThanThisOne ? {visibility: "hidden"} : {}}>
                <SwapVerticallyActionButton
                    onClick={() =>
                        props.handleShiftStart({
                            domainIndex: props.libraryDomainUse.orderNumber,
                            goalIndex: props.libraryGoalUse.orderNumber,
                            indicatorIndex: props.libraryIndicatorUse.orderNumber,
                        })
                    }
                />
            </div>
            <Box className={classes.indicatorItemName}>{props.libraryIndicatorUse.indicator.name}</Box>
            <Box className={classes.infoButton}>
                <InfoActionButton onClick={handleInfoOpen} />
            </Box>
            <Box>
                <DeleteActionButton onClick={handleDelete} />
            </Box>
            <IndicatorDialog
                indicator={props.libraryIndicatorUse.indicator}
                handleClose={handleReset}
                open={state.name === "info"}
            />
            <ConfirmationDialog
                title={t("indicator.confirm_delete_use.title")}
                text={t("indicator.confirm_delete_use.text", {title: props.libraryIndicatorUse.indicator.name})}
                open={state.name === "delete_confirm"}
                onAgree={handleDeleteConfirm}
                onDisagree={handleReset}
                id={"confirm-delete-dialog-" + props.libraryIndicatorUse.id}
            />
            {state.error !== null && (
                <InformationDialog
                    id={`delete-inform-dialog-${props.libraryIndicatorUse.id}`}
                    title={t(`indicator.confirm_delete.inform.title`)}
                    text={t(`indicator.confirm_delete.inform.text`, {
                        title: props.libraryIndicatorUse.indicator.name ?? "",
                        count: state.error?.relatedEntities.length ?? 0,
                    })}
                    open={state.name === "delete_inform"}
                    onClose={handleReset}
                >
                    <LibraryIndicatorRenderDelete
                        entity={{
                            name: props.libraryIndicatorUse.indicator.name,
                            id: props.libraryIndicatorUse.id,
                        }}
                        error={state.error}
                    />
                </InformationDialog>
            )}
            {state.name.endsWith("ing") && <LoadingBackdrop />}
        </Box>
    );
};
